import React, { useMemo, useState, useEffect } from 'react';
import { Typography, Box, Chip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import moment from 'moment';
import 'moment/locale/pt-br';
import HistoryItem from './HistoryItem';

// Detecta o tema atual baseado nas variáveis CSS
const detectTheme = () => {
  const primaryColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--mexx-green-primary')
    .trim();
  
  const isBlueTheme = primaryColor.includes('2979ff') || primaryColor.includes('#2979ff');
  return isBlueTheme ? 'blue' : 'green';
};

// Cores do tema para a linha central
const getTimelineColors = (theme) => {
  if (theme === 'blue') {
    return {
      lineColor: '#E3F2FD',
      lineGradientStart: '#BBDEFB',
      lineGradientMid: '#E3F2FD',
      lineGradientEnd: '#F5F5F5',
      chipBg: '#1E88E5',
    };
  } else {
    return {
      lineColor: '#E8F5E9',
      lineGradientStart: '#C8E6C9',
      lineGradientMid: '#E8F5E9',
      lineGradientEnd: '#F5F5F5',
      chipBg: '#558B2F',
    };
  }
};

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2, 1.5),
    maxWidth: '100%',
    position: 'relative',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(4),
  },
  groupConnector: {
    position: 'absolute',
    left: '50%',
    top: 0,
    bottom: 0,
    width: '2px', // Reduzido de 3px para 2px
    transform: 'translateX(-50%)',
    zIndex: 0,
    borderRadius: '1px',
  },
  dateHeader: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(4),
    marginTop: theme.spacing(1),
    position: 'relative',
    zIndex: 1,
  },
  dateChip: {
    fontWeight: 600,
    fontSize: '0.75rem',
    height: 28,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    borderRadius: '14px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    letterSpacing: '0.3px',
  },
  groupContainer: {
    marginBottom: theme.spacing(5),
    position: 'relative',
  },
  firstGroupContainer: {
    marginTop: 0,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(6),
    textAlign: 'center',
  },
}));

function RequesterHistoryList({ items, onItemClick, onOpenInNew }) {
  const classes = useStyles();
  const [currentTheme, setCurrentTheme] = useState('green');

  useEffect(() => {
    const detected = detectTheme();
    setCurrentTheme(detected);
    
    const observer = new MutationObserver(() => {
      const newTheme = detectTheme();
      if (newTheme !== currentTheme) {
        setCurrentTheme(newTheme);
      }
    });
    
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style']
    });
    
    return () => observer.disconnect();
  }, [currentTheme]);

  const timelineColors = getTimelineColors(currentTheme);

  const groupedItems = useMemo(() => {
    const groups = {};
    items.forEach((item) => {
      const dateKey = moment(item.createdat).format('YYYY-MM-DD');
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(item);
    });
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
  }, [items]);

  const getDateLabel = (dateString) => {
    moment.locale('pt-br');
    const date = moment(dateString);
    if (moment().isSame(date, 'day')) return 'Hoje';
    if (moment().subtract(1, 'days').isSame(date, 'day')) return 'Ontem';
    return date.format('DD [de] MMMM, YYYY');
  };

  let globalIndex = 0;

  if (!items || items.length === 0) {
    return (
      <Box className={classes.emptyState}>
        <Typography variant="body1" color="textSecondary">
          Nenhum item encontrado
        </Typography>
      </Box>
    );
  }

  return (
    <div className={classes.container}>
      {/* Linha central da timeline com gradiente suave */}
      <Box 
        className={classes.groupConnector}
        sx={{
          background: `linear-gradient(180deg, 
            ${timelineColors.lineGradientStart} 0%, 
            ${timelineColors.lineGradientMid} 15%, 
            ${timelineColors.lineGradientMid} 85%, 
            ${timelineColors.lineGradientEnd} 100%
          )`,
        }}
      />

      {groupedItems.map(([dateKey, groupItems], groupIndex) => {
        return (
          <Box 
            key={dateKey} 
            className={`${classes.groupContainer} ${groupIndex === 0 ? classes.firstGroupContainer : ''}`}
          >
            {/* Cabeçalho de data */}
            <div className={classes.dateHeader}>
              <Chip
                label={getDateLabel(dateKey)}
                className={classes.dateChip}
                sx={{
                  backgroundColor: timelineColors.chipBg,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: timelineColors.chipBg,
                  },
                }}
              />
            </div>

            {/* Items do grupo */}
            {groupItems.map((item) => {
              const currentGlobalIndex = globalIndex++;
              return (
                <HistoryItem
                  key={item.id || `${dateKey}-${currentGlobalIndex}`}
                  item={item}
                  index={currentGlobalIndex}
                  onClick={onItemClick}
                  onOpenInNew={onOpenInNew}
                />
              );
            })}
          </Box>
        );
      })}
    </div>
  );
}

export default RequesterHistoryList;